import { encrypt, decrypt } from 'crypto-js/aes';
import { parse } from 'crypto-js/enc-utf8';
import md5 from 'crypto-js/md5';
import UTF8 from 'crypto-js/enc-utf8';
import Base64 from 'crypto-js/enc-base64';
import { mode, pad } from 'crypto-js'; // 引入 Mode 和 Padding

export interface EncryptionParams {
  key: string;
  iv: string;
}

export class AesEncryption {
  private key: any;
  private iv: any;

  constructor(opt: Partial<EncryptionParams> = {}) {
    const { key, iv } = opt;
    if (key) {
      // 从Base64解码密钥，对应OC中的 initWithBase64EncodedString
      this.key = Base64.parse(key);
    }
    if (iv) {
      this.iv = parse(iv);
    }
  }

  /**
   * AES加密字符串（对应OC的AESEncryptWithStr方法）
   * @param str 要加密的字符串
   * @returns URL安全的Base64编码的密文
   */
  AESEncryptWithStr(str: string): string {
    try {
      // 将字符串转换为UTF8数据，对应OC的dataUsingEncoding:NSUTF8StringEncoding
      const data = UTF8.parse(str);

      // 执行AES加密
      const encryptedData = this.AESEncryptData(data);

      if (encryptedData) {
        // 使用URL安全的base64编码，对应OC的base64EncodedStringWithOptions:0
        const base64String = encryptedData.toString();

        // 将标准base64中的特殊字符替换为URL安全字符，对应OC的makeBase64URLSafe
        return this.makeBase64URLSafe(base64String);
      }

      return '';
    } catch (error) {
      console.error('AES加密失败:', error);
      return '';
    }
  }

  /**
   * AES加密数据（对应OC的AESEncryptData:withBase64Key方法）
   * @param data 要加密的数据
   * @returns 加密后的数据
   */
  private AESEncryptData(data: any): any {
    if (!this.key) {
      console.error('密钥未设置');
      return null;
    }

    try {
      // 执行加密 (ECB模式，不使用IV)，对应OC的CCCrypt
      const encrypted = encrypt(data, this.key, {
        mode: mode.ECB,           // ECB模式
        padding: pad.Pkcs7        // PKCS7填充
      });

      return encrypted;
    } catch (error) {
      console.error('AES数据加密失败:', error);
      return null;
    }
  }

  /**
   * 将标准base64转换为URL安全的base64（对应OC的makeBase64URLSafe方法）
   * @param base64String 标准Base64字符串
   * @returns URL安全的Base64字符串
   */
  private makeBase64URLSafe(base64String: string): string {
    if (!base64String) {
      return '';
    }

    // 替换特殊字符：+ -> -, / -> _, 移除填充字符 =
    let urlSafe = base64String.replace(/\+/g, '-');
    urlSafe = urlSafe.replace(/\//g, '_');
    urlSafe = urlSafe.replace(/=/g, '');

    return urlSafe;
  }

  /**
   * 将URL安全的base64转换回标准base64
   * @param urlSafeBase64 URL安全的Base64字符串
   * @returns 标准Base64字符串
   */
  private fromUrlSafeBase64(urlSafeBase64: string): string {
    if (!urlSafeBase64) {
      return '';
    }

    // 替换特殊字符：- -> +, _ -> /
    let base64 = urlSafeBase64.replace(/-/g, '+');
    base64 = base64.replace(/_/g, '/');

    // 添加填充字符
    const padLength = 4 - (base64.length % 4);
    if (padLength !== 4) {
      base64 += '='.repeat(padLength);
    }

    return base64;
  }

  /**
   * AES解密（对应OC的解密方法）
   * @param encryptedStr URL安全的Base64编码的密文
   * @returns 解密后的明文
   */
  AESDecryptWithStr(encryptedStr: string): string {
    try {
      // 将URL安全的base64转换回标准base64
      const standardBase64 = this.fromUrlSafeBase64(encryptedStr);

      // 解密
      const decrypted = decrypt(standardBase64, this.key, {
        mode: mode.ECB,
        padding: pad.Pkcs7
      });

      // 转换为UTF8字符串
      return decrypted.toString(UTF8);
    } catch (error) {
      console.error('AES解密失败:', error);
      return '';
    }
  }

  // 保持向后兼容的方法
  encryptByAES(plainText: string): string {
    return this.AESEncryptWithStr(plainText);
  }

  decryptByAES(cipherText: string): string {
    return this.AESDecryptWithStr(cipherText);
  }
}

/**
 * 将标准Base64转换为URL安全的Base64
 * @param base64 标准Base64字符串
 * @returns URL安全的Base64字符串
 */
export function toUrlSafeBase64(base64: string): string {
  return base64
    .replace(/\+/g, '-')  // 将 + 替换为 -
    .replace(/\//g, '_')  // 将 / 替换为 _
    .replace(/=/g, '');   // 移除填充字符 =
}

/**
 * 将URL安全的Base64转换回标准Base64
 * @param urlSafeBase64 URL安全的Base64字符串
 * @returns 标准Base64字符串
 */
export function fromUrlSafeBase64(urlSafeBase64: string): string {
  let base64 = urlSafeBase64
    .replace(/-/g, '+')  // 将 - 替换为 +
    .replace(/_/g, '/'); // 将 _ 替换为 /

  // 添加填充字符
  const padLength = 4 - (base64.length % 4);
  if (padLength !== 4) {
    base64 += '='.repeat(padLength);
  }

  return base64;
}

export const encryption = new AesEncryption({ key: 'jz+GKqSdoEs/pw/uIWgdkQ==' });

/**
 * 便捷的AES加密函数（对应OC的AESEncryptWithStr方法）
 * @param plainText 明文
 * @param keyBase64 Base64编码的密钥，可选，默认使用配置的密钥
 * @returns URL安全的Base64编码的密文
 */
export function encryptECB(plainText: string, keyBase64?: string): string {
  const aes = keyBase64 ? new AesEncryption({ key: keyBase64 }) : encryption;
  return aes.AESEncryptWithStr(plainText);
}

/**
 * 便捷的AES解密函数（对应OC的解密方法）
 * @param cipherText URL安全的Base64编码的密文
 * @param keyBase64 Base64编码的密钥，可选，默认使用配置的密钥
 * @returns 明文
 */
export function decryptECB(cipherText: string, keyBase64?: string): string {
  const aes = keyBase64 ? new AesEncryption({ key: keyBase64 }) : encryption;
  return aes.AESDecryptWithStr(cipherText);
}

export function encryptByBase64(cipherText: string) {
  return UTF8.parse(cipherText).toString(Base64);
}

export function decodeByBase64(cipherText: string) {
  return Base64.parse(cipherText).toString(UTF8);
}

export function encryptByMd5(password: string) {
  return md5(password).toString();
}
